import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const BookingContext = createContext(null);

const initialState = {
  origin: '',
  destination: '',
  date: '',
  passengers: 1,
  selectedTrip: null,
  selectedSeats: [],
  passengerDetails: [],
  contactPhone: '',
  contactEmail: '',
  emergencyName: '',
  emergencyPhone: '',
  emergencyEmail: '',
  pickupPoint: null,
  dropoffPoint: null,
  booking: null,
};

export function BookingProvider({ children }) {
  const [state, setState] = useState(initialState);

  const setSearch = useCallback(({ origin, destination, date, passengers }) => {
    setState((prev) => ({
      ...prev,
      origin,
      destination,
      date,
      passengers: Math.max(1, Number(passengers) || 1),
      selectedTrip: null,
      selectedSeats: [],
      passengerDetails: [],
      pickupPoint: null,
      dropoffPoint: null,
      booking: null,
    }));
  }, []);

  const selectTrip = useCallback((trip) => {
    setState((prev) => ({
      ...prev,
      selectedTrip: trip,
      selectedSeats: [],
      passengerDetails: [],
      pickupPoint: trip?.pickupPoints?.[0]
        ? { ...trip.pickupPoints[0] }
        : null,
      dropoffPoint: trip?.dropoffPoints?.[0]
        ? { ...trip.dropoffPoints[0] }
        : null,
      booking: null,
    }));
  }, []);

  const toggleSeat = useCallback((label) => {
    setState((prev) => {
      const seats = [...prev.selectedSeats];
      const idx = seats.indexOf(label);
      if (idx >= 0) {
        seats.splice(idx, 1);
      } else {
        if (seats.length >= prev.passengers) seats.shift();
        seats.push(label);
      }
      return { ...prev, selectedSeats: seats };
    });
  }, []);

  const setPassengerDetails = useCallback((details) => {
    setState((prev) => ({ ...prev, passengerDetails: details }));
  }, []);

  const setStops = useCallback(({ pickup, dropoff }) => {
    setState((prev) => ({
      ...prev,
      ...(pickup !== undefined ? { pickupPoint: pickup } : {}),
      ...(dropoff !== undefined ? { dropoffPoint: dropoff } : {}),
    }));
  }, []);

  const setContact = useCallback(({ phone, email }) => {
    setState((prev) => ({
      ...prev,
      ...(phone !== undefined ? { contactPhone: phone } : {}),
      ...(email !== undefined ? { contactEmail: email } : {}),
    }));
  }, []);

  const setEmergencyContact = useCallback(({ name, phone, email }) => {
    setState((prev) => ({
      ...prev,
      ...(name !== undefined ? { emergencyName: name } : {}),
      ...(phone !== undefined ? { emergencyPhone: phone } : {}),
      ...(email !== undefined ? { emergencyEmail: email } : {}),
    }));
  }, []);

  const setBooking = useCallback((booking) => {
    setState((prev) => {
      const withTrip =
        booking && !booking.trip && prev.selectedTrip
          ? {
              ...booking,
              trip: prev.selectedTrip,
              seats:
                booking.seats?.length > 0
                  ? booking.seats
                  : [...prev.selectedSeats],
              passengers:
                booking.passengers?.length > 0
                  ? booking.passengers
                  : prev.passengerDetails,
            }
          : booking;
      return { ...prev, booking: withTrip };
    });
  }, []);

  const reset = useCallback(() => {
    setState(initialState);
  }, []);

  const total = useMemo(() => {
    const price = Number(state.selectedTrip?.price) || 0;
    return price * state.selectedSeats.length;
  }, [state.selectedTrip, state.selectedSeats]);

  const value = useMemo(
    () => ({
      ...state,
      total,
      setSearch,
      selectTrip,
      toggleSeat,
      setPassengerDetails,
      setStops,
      setContact,
      setEmergencyContact,
      setBooking,
      reset,
    }),
    [
      state,
      total,
      setSearch,
      selectTrip,
      toggleSeat,
      setPassengerDetails,
      setStops,
      setContact,
      setEmergencyContact,
      setBooking,
      reset,
    ]
  );

  return (
    <BookingContext.Provider value={value}>{children}</BookingContext.Provider>
  );
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) {
    throw new Error('useBooking must be used within BookingProvider');
  }
  return ctx;
}
